import { Bot, User, Wrench, ShieldCheck } from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import { cn } from "@/lib/cn"
import { ToolCallChip } from "@/components/domain/tool-call-chip"
import type { ChatBubble } from "@/types/test_lab"

type Props = {
  msg: ChatBubble
  /** Which side the user role lands on. Test tab → "right" (operator-as-user). */
  userSide?: "left" | "right"
  userLabel?:      string
  assistantLabel?: string
  /** Render the tool_call / tool_result chips below the bubble.
   *  Defaults to true (designer + test panes need them); the production
   *  ticket viewer flips this off for non-admin operators. */
  showToolCalls?:  boolean
}

// One message turn in the support-inbox style.
//
// Layout (inline / Zendesk-style — every role flows down a single column):
//   [avatar] Name · time
//            ┌──────────────────┐
//            │ content          │
//            └──────────────────┘
//            ▸ tool_call chip
//
// Avatar always anchors on the left. The `userSide` prop is kept on the
// type for back-compat with existing callsites, but is no longer read —
// the inline layout doesn't have a "right side" anymore.
export function ChatMessageBubble({
  msg,
  userSide: _userSide,
  userLabel = "You (as user)",
  assistantLabel = "Agent",
  showToolCalls = true,
}: Props) {
  void _userSide
  const isUser      = msg.role === "user"
  const isAssistant = msg.role === "assistant"
  const isHuman     = msg.role === "human_agent"
  const isSystem    = msg.role === "system"
  const isTool      = msg.role === "tool"

  const label =
    msg.authorName?.trim()
      ? msg.authorName
      : isUser ? userLabel
      : isAssistant ? assistantLabel
      : isHuman ? "Human agent"
      : isSystem ? "System"
      : isTool ? "Tool"
      : msg.role

  const Icon = isUser ? User : isHuman ? User : isTool ? Wrench : isSystem ? ShieldCheck : Bot

  // Avatar tint: red for operator-as-user / human, gold for agent, neutral else.
  const avatarTone = isUser || isHuman
    ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
    : isAssistant
      ? "border-vip-gold/40 bg-vip-goldMute text-vip-goldSoft"
      : "border-border bg-app-panel2 text-app-muted"

  // Initial used by the letter-tile fallback when no avatar URL is supplied.
  const initial = (label?.toString().trim()[0] ?? "?").toUpperCase()

  // Bubble: operator gets a red left edge; human a gold edge; agent stays neutral.
  const bubbleTone = isHuman
    ? "border-l-2 border-l-vip-gold/60 border-y border-r border-border bg-app-panel2 text-app-text"
    : isUser
      ? "border-l-2 border-l-brand-red/40 border-y border-r border-border bg-app-panel2 text-app-text"
      : isAssistant
        ? "border border-border bg-app-panel2 text-app-text"
        : "border border-border bg-white/[0.03] text-app-muted"

  const time = new Date(msg.createdAt).toLocaleTimeString([], {
    hour: "numeric", minute: "2-digit",
  })

  const toolCalls   = Array.isArray(msg.toolCalls)   ? msg.toolCalls   as Array<Record<string, unknown>> : []
  const toolResults = Array.isArray(msg.toolResults) ? msg.toolResults as Array<Record<string, unknown>> : []

  // Three-tier avatar: real photo URL → letter-tile (when we have a name to
  // derive a letter from) → role icon fallback. The outer wrapper keeps the
  // 36×36 footprint identical across all three so the column doesn't jump.
  const Avatar = msg.avatarUrl ? (
    <img
      src={msg.avatarUrl}
      alt={initial}
      loading="lazy"
      className="h-12 w-12 shrink-0 rounded-full border border-border bg-app-panel2 object-cover"
    />
  ) : msg.authorName ? (
    <div
      className={cn(
        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full border text-base font-semibold",
        avatarTone,
      )}
    >
      {initial}
    </div>
  ) : (
    <div
      className={cn(
        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full border",
        avatarTone,
      )}
    >
      <Icon size={20} strokeWidth={1.8} />
    </div>
  )

  return (
    <div className="flex w-full gap-3">
      {Avatar}
      <div className="flex max-w-[640px] min-w-0 flex-col gap-1.5">
        <div className="flex items-baseline gap-2 text-[11px]">
          <span className="font-semibold text-app-text">{label}</span>
          <span className="text-app-subtle">{time}</span>
        </div>
        {msg.content && (
          <div className={cn("rounded-card px-4 py-2.5 text-sm", bubbleTone)}>
            {!isTool ? (
              <div className="prose-chat leading-relaxed">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    a: ({ node: _node, href: _href, children, ...props }) => (
                      isSystem
                        // System rating-prompt links shouldn't be clickable from the
                        // operator side (only the end user should be able to rate).
                        // Render as a dull-white span so the visual layout still
                        // reads like buttons but nothing fires.
                        ? <span {...props} className="text-app-muted">{children}</span>
                        : <a {...props} href={_href} target="_blank" rel="noopener noreferrer" className="text-brand-red underline-offset-2 hover:underline">{children}</a>
                    ),
                    code: ({ node: _node, className, children, ...props }) => (
                      <code {...props} className={cn("rounded bg-black/30 px-1 py-0.5 font-mono text-[0.85em]", className)}>{children}</code>
                    ),
                    pre: ({ node: _node, ...props }) => (
                      <pre {...props} className="my-2 overflow-x-auto rounded-control bg-black/30 p-2 font-mono text-[12px]" />
                    ),
                    ul: ({ node: _node, ...props }) => <ul {...props} className="my-1 list-disc pl-5 space-y-0.5" />,
                    ol: ({ node: _node, ...props }) => <ol {...props} className="my-1 list-decimal pl-5 space-y-0.5" />,
                    p:  ({ node: _node, ...props }) => <p {...props} className="my-1 first:mt-0 last:mb-0" />,
                    h1: ({ node: _node, ...props }) => <p {...props} className="my-1 font-semibold" />,
                    h2: ({ node: _node, ...props }) => <p {...props} className="my-1 font-semibold" />,
                    h3: ({ node: _node, ...props }) => <p {...props} className="my-1 font-semibold" />,
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
            )}
          </div>
        )}
        {showToolCalls && (toolCalls.length > 0 || toolResults.length > 0) && (
          <div className="w-full space-y-1.5">
            {pairCallsWithResults(toolCalls, toolResults).map((pair, i) => (
              <ToolCallChip
                key={`tool-${i}`}
                toolCall={pair.call}
                toolResult={pair.result}
                timestamp={msg.createdAt}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Pair each tool_call with its matching tool_result (same `id`, the
// Anthropic tool_use_id). Calls without a result render as in-flight;
// orphan results (legacy data with no call alongside) still render so
// nothing is lost. Order follows the call list, then any leftover
// orphan results.
type ToolCall   = { id?: string; name?: string; input?: unknown }
type ToolResult = { id?: string; name?: string; output?: unknown; is_error?: boolean }

function pairCallsWithResults(
  calls: Array<Record<string, unknown>>,
  results: Array<Record<string, unknown>>,
): Array<{ call?: ToolCall; result?: ToolResult }> {
  const remainingResults = [...results] as ToolResult[]
  const pairs: Array<{ call?: ToolCall; result?: ToolResult }> = []

  for (const c of calls as ToolCall[]) {
    let matchIdx = -1
    if (c.id) matchIdx = remainingResults.findIndex((r) => r.id === c.id)
    if (matchIdx === -1) matchIdx = remainingResults.findIndex((r) => r.name === c.name)
    const match = matchIdx >= 0 ? remainingResults.splice(matchIdx, 1)[0] : undefined
    pairs.push({ call: c, result: match })
  }
  // Any results without a matching call (split-message convos) tail the list.
  for (const orphan of remainingResults) pairs.push({ result: orphan })

  return pairs
}
